import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@material-ui/core';
import { apiFetch } from '../../utils/apiFetch';
import './global/global.css';

const withStyles = makeStyles(() => ({
    root: {
        padding: '20px',
        maxWidth: '100%',
        margin: '0',
        marginBottom: '40px',
    },
    error: {
        color: 'red',
        marginTop: '10px',
    },
    tableContainer: {
        marginTop: '20px',
        maxWidth: '100%',
        overflowX: 'auto',
    },
    table: {
        minWidth: 650,
    },
    tableHeader: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
    },
    activeBadge: {
        backgroundColor: '#1faa60',
        color: 'white',
    },
    recentBadge: {
        backgroundColor: '#ff9800',
        color: 'white',
    },
    inactiveBadge: {
        backgroundColor: '#d32f2f',
        color: 'white',
    },
    statsContainer: {
        display: 'flex',
        gap: '20px',
        marginTop: '20px',
        flexWrap: 'wrap',
    },
    statBox: {
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        minWidth: '150px',
    },
    statLabel: {
        fontSize: '14px',
        color: '#707070',
        marginBottom: '5px',
    },
    statValue: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
    },
    childRow: {
        backgroundColor: '#f9f9f9',
        '&:nth-of-type(even)': {
            backgroundColor: '#f5f5f5',
        },
    },
    expandIcon: {
        cursor: 'pointer',
        marginRight: '8px',
        fontSize: '18px',
        fontWeight: 'bold',
        userSelect: 'none',
    },
    statusCheckmark: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#1faa60',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold',
    },
}));

const AdminAnalytics = () => {
    const classes = withStyles();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [expandedUsers, setExpandedUsers] = useState(new Set());

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                const response = await apiFetch(`${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/analytics`);

                if (!response.ok) {
                    throw new Error('Failed to fetch analytics data');
                }

                const data = await response.json();
                setAnalyticsData(data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    const getRecencyBadge = (lastActiveStr) => {
        if (!lastActiveStr) {
            return <Chip label="No activity" className={classes.inactiveBadge} size="small" />;
        }

        const lastActive = new Date(lastActiveStr);
        const now = new Date();
        const daysSince = Math.floor((now - lastActive) / (1000 * 60 * 60 * 24));

        if (daysSince < 7) {
            return <Chip label="Active" className={classes.activeBadge} size="small" />;
        } else if (daysSince < 30) {
            return <Chip label="Recent" className={classes.recentBadge} size="small" />;
        } else {
            return <Chip label="Inactive" className={classes.inactiveBadge} size="small" />;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "No data";
        return new Date(dateString).toLocaleDateString();
    };

    const toggleExpand = (email) => {
        setExpandedUsers(prev => {
            const next = new Set(prev);
            if (next.has(email)) {
                next.delete(email);
            } else {
                next.add(email);
            }
            return next;
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month}/${day}`;
    };

    if (loading) {
        return (
            <div className="dashboard-root">
                <div className="db-container">
                    <div className="dashboard">
                        <div className="dashboard-container">
                            <Typography>Loading analytics...</Typography>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-root">
                <div className="db-container">
                    <div className="dashboard">
                        <div className="dashboard-container">
                            <Typography className={classes.error}>Error: {error}</Typography>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-root">
            <div className="db-container">
                <div className="dashboard">
                    <div className="dashboard-container">
                        <div className="dashboard-content">
                            <h3>Analytics Dashboard</h3>
                            <p style={{ color: "#707070" }}>
                                User activity metrics, DPM progression funnel, and engagement trends.
                            </p>
                        </div>

                        <Paper className={classes.root}>
                            <Typography variant="h5" gutterBottom style={{ textAlign: 'left' }}>
                                Activity Timeline
                            </Typography>
                            <div className={classes.statsContainer}>
                                <div className={classes.statBox}>
                                    <div className={classes.statLabel}>Last 7 Days</div>
                                    <div className={classes.statValue}>
                                        {analyticsData?.activity_timeline?.last_7_days || 0}
                                    </div>
                                </div>
                                <div className={classes.statBox}>
                                    <div className={classes.statLabel}>Last 30 Days</div>
                                    <div className={classes.statValue}>
                                        {analyticsData?.activity_timeline?.last_30_days || 0}
                                    </div>
                                </div>
                                <div className={classes.statBox}>
                                    <div className={classes.statLabel}>Last 90 Days</div>
                                    <div className={classes.statValue}>
                                        {analyticsData?.activity_timeline?.last_90_days || 0}
                                    </div>
                                </div>
                            </div>
                        </Paper>

                        <Paper className={classes.root}>
                            <Typography variant="h5" gutterBottom style={{ textAlign: 'left' }}>
                                User Activity
                            </Typography>
                            <TableContainer className={classes.tableContainer}>
                                <Table className={classes.table} aria-label="user activity table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.tableHeader}>Email / Property</TableCell>
                                            <TableCell className={classes.tableHeader}>Total Deals</TableCell>
                                            <TableCell className={classes.tableHeader}>Recent Deals (30d)</TableCell>
                                            <TableCell className={classes.tableHeader}>Document Creation</TableCell>
                                            <TableCell className={classes.tableHeader}>Tracking</TableCell>
                                            <TableCell className={classes.tableHeader}>Closed</TableCell>
                                            <TableCell className={classes.tableHeader}>Last Active</TableCell>
                                            <TableCell className={classes.tableHeader}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analyticsData?.top_users?.map((user) => (
                                            <React.Fragment key={user.email}>
                                                <TableRow>
                                                    <TableCell>
                                                        <span
                                                            onClick={() => toggleExpand(user.email)}
                                                            className={classes.expandIcon}
                                                        >
                                                            {expandedUsers.has(user.email) ? '−' : '+'}
                                                        </span>
                                                        {user.email}
                                                    </TableCell>
                                                    <TableCell>{user.total_deals}</TableCell>
                                                    <TableCell>{user.recent_deals}</TableCell>
                                                    <TableCell>{user.document_creation_count || 0}</TableCell>
                                                    <TableCell>{user.tracking_count || 0}</TableCell>
                                                    <TableCell>{user.closed_count || 0}</TableCell>
                                                    <TableCell>{formatDate(user.last_active)}</TableCell>
                                                    <TableCell>{getRecencyBadge(user.last_active)}</TableCell>
                                                </TableRow>
                                                {expandedUsers.has(user.email) && user.deals && user.deals.map((deal, idx) => (
                                                    <TableRow key={`deal-${deal.id}`} className={classes.childRow}>
                                                        <TableCell style={{ paddingLeft: '40px', borderLeft: '3px solid #28a382' }}>
                                                            <div>{deal.display_address || deal.property_address}</div>
                                                            <div style={{ fontSize: '12px', color: '#707070', marginTop: '4px' }}>
                                                                Tgt: {formatShortDate(deal.target_date)} • Cre: {formatShortDate(deal.created_at)}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>—</TableCell>
                                                        <TableCell>—</TableCell>
                                                        <TableCell>
                                                            {deal.status === 'document_creation' && (
                                                                <span className={classes.statusCheckmark}>✓</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {deal.status === 'tracking' && (
                                                                <span className={classes.statusCheckmark}>✓</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {deal.status === 'closed' && (
                                                                <span className={classes.statusCheckmark}>✓</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{formatDate(deal.updated_at)}</TableCell>
                                                        <TableCell></TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminAnalytics;
